import {AvatarStack} from '@primer/react'
import {useMemo} from 'react'
import {graphql, usePaginationFragment} from 'react-relay'

import type {Assignees$key} from './__generated__/Assignees.graphql'
import {Assignee} from './Assignee'
import styles from './Assignees.module.css'

export type AssigneesFilter = 'agents' | 'users'

type assigneesProps = {
  assigneeskey: Assignees$key
  /**
   * Filter which assignees to show:
   * - 'users': only non-agent assignees
   * - 'agents': only agent/copilot assignees
   */
  filter?: AssigneesFilter
  /**
   * Href getter for the assignee badge links
   * @param name - name of the assignee
   * @returns URL to the assignee
   */
  getAssigneeHref: (assignee: string) => string
}

function isAgentAssignee(assignee: {isAgent?: boolean; isCopilot?: boolean}) {
  return assignee.isAgent || assignee.isCopilot
}

export function Assignees({assigneeskey, getAssigneeHref, filter}: assigneesProps) {
  const {data} = usePaginationFragment(
    // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
    graphql`
      fragment Assignees on Assignable
      @argumentDefinitions(assigneePageSize: {type: "Int"}, cursor: {type: "String"})
      @refetchable(queryName: "IssueAssigneePaginatedQuery") {
        assignedActors(first: $assigneePageSize, after: $cursor) @connection(key: "IssueAssignees_assignedActors") {
          edges {
            node {
              __typename
              ... on Actor {
                __typename
                id
                login
                # eslint-disable-next-line relay/unused-fields - used by similar fragments, needed here too
                name
                # eslint-disable-next-line relay/unused-fields - used by similar fragments, needed here too
                profileResourcePath
                # eslint-disable-next-line relay/unused-fields - used by similar fragments, needed here too
                avatarUrl(size: 64)
                ... on Bot {
                  isCopilot
                  isAgent
                  displayName
                }
              }
            }
          }
        }
      }
    `,
    assigneeskey,
  )
  const assignees = useMemo(() => {
    const allAssignees = (data.assignedActors?.edges || []).flatMap(a => (a && a.node ? a.node : []))
    if (filter === 'agents') return allAssignees.filter(a => isAgentAssignee(a))
    if (filter === 'users') return allAssignees.filter(a => !isAgentAssignee(a))
    return allAssignees
  }, [data.assignedActors?.edges, filter])

  if (!assignees.length) {
    return null
  }

  return (
    <AvatarStack alignRight className={styles.AssigneesAvatarStack} shape={filter === 'agents' ? 'square' : 'circle'}>
      {assignees.map(assignee => (
        <Assignee
          key={assignee.id}
          login={assignee.login || ''}
          avatarUrl={assignee.avatarUrl || ''}
          isCopilot={assignee.isCopilot || false}
          isAgent={assignee.isAgent || false}
          displayName={assignee.displayName}
          getAssigneeHref={getAssigneeHref}
        />
      ))}
    </AvatarStack>
  )
}
